import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { Link as RouterLink, useParams } from "react-router-dom";
import api from "../bootstrapApi";
import { addCartItem, useShopCart } from "../shopCart";

export default function ShopCategoryPage() {
  const accent = "#e86026";
  const support = "#656e8f";
  const { categoryId } = useParams();
  const [products, setProducts] = useState([]);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState("");
  const [quantities, setQuantities] = useState({});
  const cartItems = useShopCart();

  useEffect(() => {
    api
      .get(`/api/public/categories/${categoryId}/products`)
      .then((response) => {
        setProducts(response.data);
        setMessage(null);
      })
      .catch(() =>
        setMessage({ type: "error", text: "Les produits de cette categorie n'ont pas pu etre charges." })
      );
  }, [categoryId]);

  const categoryName = products[0]?.category_name || "Categorie";
  const cartCount = cartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const categoryTypeLabel = useMemo(() => {
    const types = [...new Set(products.map((product) => product.product_type).filter(Boolean))];
    if (types.length === 1) {
      return types[0] === "custom" ? "Sur mesure" : "Standard";
    }
    if (types.length > 1) {
      return "Mixte";
    }
    return null;
  }, [products]);

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();

    return products.filter((product) => {
      if (!query) {
        return true;
      }

      return (
        product.name?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query)
      );
    });
  }, [products, search]);

  const handleQuantityChange = (productId, value) => {
    setQuantities((current) => ({
      ...current,
      [productId]: Math.max(1, Number(value || 1)),
    }));
  };

  return (
    <Box sx={{ display: "grid", justifyContent: "center" }}>
      {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

      <Box
        sx={{
          width: "100%",
          maxWidth: 430,
          borderRadius: { xs: 0, md: "36px" },
          minHeight: "calc(100vh - 48px)",
          background: "linear-gradient(180deg, #fff7f2 0%, #f7efe9 100%)",
          boxShadow: { xs: "none", md: "0 30px 60px rgba(101,110,143,0.12)" },
          p: 2.2,
          display: "grid",
          gap: 2.2,
        }}
      >
        <Stack direction="row" spacing={1.2} alignItems="center">
          <IconButton
            component={RouterLink}
            to="/shop"
            sx={{
              width: 42,
              height: 42,
              bgcolor: "#fff",
              border: "1px solid #f0ddd3",
              boxShadow: "0 8px 16px rgba(101,110,143,0.06)",
            }}
          >
            <ArrowBackRoundedIcon sx={{ color: support }} />
          </IconButton>

          <TextField
            placeholder={`Rechercher dans ${categoryName}`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#fff",
                borderRadius: "18px",
                boxShadow: "0 8px 16px rgba(101,110,143,0.05)",
              },
            }}
          />

          <IconButton
            component={RouterLink}
            to="/shop/cart"
            sx={{
              width: 42,
              height: 42,
              bgcolor: "#fff",
              border: "1px solid #f0ddd3",
              boxShadow: "0 8px 16px rgba(101,110,143,0.06)",
              position: "relative",
            }}
          >
            <ShoppingBagOutlinedIcon sx={{ color: accent }} />
            {cartCount ? (
              <Box
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  minWidth: 18,
                  height: 18,
                  px: 0.5,
                  borderRadius: "999px",
                  bgcolor: accent,
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 800,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {cartCount}
              </Box>
            ) : null}
          </IconButton>
        </Stack>

        <Box
          sx={{
            borderRadius: "28px",
            p: 2.2,
            background: "linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)",
            border: "1px solid #f0ddd3",
            boxShadow: "0 16px 30px rgba(101,110,143,0.07)",
            display: "grid",
            gap: 1.4,
          }}
        >
          <Typography variant="overline" sx={{ color: "#7a879b", letterSpacing: "0.14em" }}>
            LISTE PRODUITS
          </Typography>
          <Typography variant="h4" sx={{ color: "#3b302f", fontWeight: 800 }}>
            {categoryName}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`${filteredProducts.length} produits`} sx={{ borderRadius: "999px", fontWeight: 700 }} />
            {categoryTypeLabel ? (
              <Chip
                label={categoryTypeLabel}
                sx={{
                  borderRadius: "999px",
                  fontWeight: 800,
                  bgcolor: categoryTypeLabel === "Sur mesure" ? "rgba(232,96,38,0.12)" : "rgba(101,110,143,0.12)",
                  color: categoryTypeLabel === "Sur mesure" ? accent : support,
                }}
              />
            ) : null}
            <Chip label="Ajout rapide au panier" sx={{ borderRadius: "999px", fontWeight: 700 }} />
          </Stack>
        </Box>

        <Box sx={{ display: "grid", gap: 1.2 }}>
          {filteredProducts.map((product, index) => {
            const quantity = quantities[product.id] || 1;
            const backgrounds = [
              "linear-gradient(180deg, #fff4ee 0%, #ffffff 100%)",
              "linear-gradient(180deg, #fff6ea 0%, #ffffff 100%)",
              "linear-gradient(180deg, #f7f1ff 0%, #ffffff 100%)",
              "linear-gradient(180deg, #f4f2fb 0%, #ffffff 100%)",
            ];

            return (
              <Box
                key={product.id}
                sx={{
                  borderRadius: "24px",
                  border: "1px solid #f0ddd3",
                  background: backgrounds[index % backgrounds.length],
                  boxShadow: "0 10px 20px rgba(101,110,143,0.06)",
                  p: 1.3,
                  display: "grid",
                  gap: 1.2,
                }}
              >
                <Box
                  component={RouterLink}
                  to={`/shop/product/${product.id}`}
                  sx={{
                    height: 164,
                    borderRadius: "20px",
                    backgroundImage: product.primary_image
                      ? `url(${product.primary_image})`
                      : "linear-gradient(135deg, rgba(232,96,38,0.18), rgba(101,110,143,0.16))",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    display: "block",
                    textDecoration: "none",
                  }}
                />

                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                  <Box>
                    <Typography sx={{ fontWeight: 800, color: "#3b302f" }}>{product.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {product.description || "Aucune description"}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 800, color: "#3b302f" }}>
                    ${Number(product.price || 0).toFixed(2)}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={product.product_type === "custom" ? "Sur mesure" : "Standard"} size="small" />
                  <Chip label={product.category_name || "Categorie"} size="small" />
                </Stack>

                <Stack direction="row" spacing={1.1} alignItems="center">
                  <TextField
                    label="Qt"
                    type="number"
                    size="small"
                    value={quantity}
                    onChange={(event) => handleQuantityChange(product.id, event.target.value)}
                    sx={{ maxWidth: 92 }}
                  />
                  <Button
                    variant="contained"
                    sx={{
                      flex: 1,
                      bgcolor: accent,
                      color: "#fff",
                      fontWeight: 800,
                      borderRadius: "14px",
                      "&:hover": { bgcolor: "#c94b19" },
                    }}
                    onClick={() =>
                      addCartItem({
                        productId: product.id,
                        name: product.name,
                        price: product.price,
                        currency: product.currency,
                        imageUrl: product.primary_image,
                        categoryName: product.category_name,
                        productType: product.product_type,
                        quantity,
                      })
                    }
                  >
                    Ajouter
                  </Button>
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
